import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Header } from '@/components/Header';
import { PhraseDetail } from '@/components/PhraseDetail';
import { MultiCategoryPhraseDetail } from '@/components/MultiCategoryPhraseDetail';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { usePhrases } from '@/context/PhrasesContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useFreePhrase } from '@/context/FreePhraseContext';
import { useAuth } from '@/context/AuthContext';
import { fetchPhraseGrouped } from '@/api/phrasesApi';
import { FavoriteButton } from '@/components/FavoriteButton';
import { ShareButton } from '@/components/ShareButton';
import { ArrowLeft, ChevronLeft, ChevronRight, Lock, Sparkles } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

const PhraseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { phrases, getPhraseById, isLoading: contextLoading } = usePhrases();
  const { t, isSpanish } = useLanguage();
  const { subscriptionStatus, trackPhraseView } = useSubscription();
  const { canViewPhrase, markPhraseViewed, remainingFreeViews, FREE_PHRASE_LIMIT } = useFreePhrase();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [phrase, setPhrase] = useState(null);
  const [groupedData, setGroupedData] = useState(null);
  const [isMultiCategory, setIsMultiCategory] = useState(false);
  const [prevPhrase, setPrevPhrase] = useState(null);
  const [nextPhrase, setNextPhrase] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Smart back navigation - go to previous page or fallback to library
  const handleBackClick = () => {
    // Check if there's meaningful history to go back to
    // window.history.length > 2 because initial load adds 1-2 entries
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      // No history - go to library
      navigate('/library');
    }
  };
  
  // Check if user has premium access (includes active, trial, gifted, and admin users)
  const isPremium = subscriptionStatus?.status === 'active' || 
                    subscriptionStatus?.status === 'trial' || 
                    subscriptionStatus?.status === 'admin' ||
                    user?.is_gifted || 
                    user?.is_admin;
  const phraseId = parseInt(id, 10);
  // Allow both authenticated users AND guests to view phrases (using FreePhraseContext)
  const canView = isPremium || canViewPhrase(phraseId);
  
  // Mark phrase as viewed when loaded (for both authenticated and guest users)
  // Also track view in backend for streak/celebration tracking (authenticated only)
  useEffect(() => {
    if (canView && phrase) {
      // Track view in backend for authenticated users (streak/celebration)
      if (isAuthenticated) {
        trackPhraseView(phraseId);
      } else {
        // For guests, still record the view count on the phrase
        apiFetch(`/api/phrases/${phraseId}/view`, { method: 'POST' }).catch(() => {});
      }
      
      // Mark locally for free users and guests
      if (!isPremium) {
        markPhraseViewed(phraseId);
      }
    }
  }, [phraseId, isPremium, canView, phrase, markPhraseViewed, isAuthenticated, trackPhraseView]);
  
  useEffect(() => {
    const loadPhraseData = async () => {
      if (phrases.length === 0) return;
      
      setIsLoading(true);
      
      try {
        // Fetch grouped phrase data from API
        const grouped = await fetchPhraseGrouped(parseInt(id, 10));
        
        if (grouped && grouped.isMultiCategory) {
          setIsMultiCategory(true);
          setGroupedData(grouped);
          setPhrase(grouped.variations[0]); // Use first variation for navigation
        } else {
          setIsMultiCategory(false);
          setGroupedData(null);
          const currentPhrase = getPhraseById(id);
          setPhrase(currentPhrase);
        }
        
        // Set up navigation
        const currentPhrase = getPhraseById(id);
        if (currentPhrase) {
          const currentIndex = phrases.findIndex(p => p.id === currentPhrase.id);
          setPrevPhrase(currentIndex > 0 ? phrases[currentIndex - 1] : null);
          setNextPhrase(currentIndex < phrases.length - 1 ? phrases[currentIndex + 1] : null);
        }
      } catch (error) {
        console.error('Error loading phrase:', error);
        // Fallback to single phrase view
        const currentPhrase = getPhraseById(id);
        setPhrase(currentPhrase);
        setIsMultiCategory(false);
      } finally {
        setIsLoading(false);
      }
      
      // Scroll to top when phrase changes
      window.scrollTo(0, 0);
    };
    
    loadPhraseData();
  }, [id, phrases, getPhraseById]);
  
  if (contextLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded w-1/3 mx-auto mb-4"></div>
              <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!phrase && !groupedData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Phrase not found</h2>
            <Button onClick={() => navigate('/')}>Back to Home</Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24">
      {phrase && (
        <Helmet>
          <title>{phrase.phrase} · ClearCommunicationApp</title>
          <meta name="description" content={phrase.actualMeaning?.slice(0, 160) || phrase.phrase} />
          <link rel="canonical" href={`https://clearcommunicationapp.com/phrase/${phrase.id}`} />

          <meta property="og:type" content="article" />
          <meta property="og:site_name" content="ClearCommunicationApp" />
          <meta property="og:title" content={`${phrase.phrase} — ClearCommunicationApp`} />
          <meta property="og:description" content={phrase.actualMeaning?.slice(0, 200) || phrase.phrase} />
          <meta property="og:url" content={`https://clearcommunicationapp.com/phrase/${phrase.id}`} />
          <meta property="og:image" content={`https://clearcommunicationapp.com/api/og/phrase/${phrase.id}.png`} />
          <meta property="og:image:secure_url" content={`https://clearcommunicationapp.com/api/og/phrase/${phrase.id}.png`} />
          <meta property="og:image:type" content="image/png" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={phrase.phrase} />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${phrase.phrase} — ClearCommunicationApp`} />
          <meta name="twitter:description" content={phrase.actualMeaning?.slice(0, 200) || phrase.phrase} />
          <meta name="twitter:image" content={`https://clearcommunicationapp.com/api/og/phrase/${phrase.id}.png`} />
        </Helmet>
      )}
      <Header />
      
      <div className="container py-8 sm:py-12">
        {/* Back Button + Actions */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={handleBackClick}
            className="-ml-2 bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-medium"
            data-testid="back-to-library-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('back')}
          </Button>
          
          {/* Action buttons - Favorite and Share */}
          {phrase && (
            <div className="flex items-center gap-2">
              {isAuthenticated && (
                <FavoriteButton 
                  phraseId={phrase.id} 
                  initialFavorited={user?.favorite_phrases?.includes(phrase.id) || false}
                  size="lg"
                />
              )}
              <ShareButton 
                phrase={phrase.phrase} 
                phraseId={phrase.id}
                size="lg"
              />
            </div>
          )}
        </div>
        
        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {/* Daily limit exhausted - show upgrade/signup prompt */}
          {!isPremium && !canView && (
            <Card className="border-2 border-primary/50 bg-gradient-to-br from-primary/10 to-primary/5 mb-8">
              <CardHeader className="text-center space-y-4 py-8">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <Lock className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">{t('dailyLimitReached')}</CardTitle>
                <CardDescription className="text-base max-w-md mx-auto">
                  {isSpanish 
                    ? 'Has visto tus 5 frases gratuitas de hoy.'
                    : "You've viewed your 5 free phrases for today."} {t('comeBackTomorrow')}
                </CardDescription>
                <div className="pt-4 space-y-3">
                  {!isAuthenticated ? (
                    <>
                      <Button 
                        size="lg" 
                        onClick={() => navigate('/register')}
                        className="w-full gap-2"
                        data-testid="signup-for-more-btn"
                      >
                        <Sparkles className="h-4 w-4" />
                        {isSpanish ? 'Crear Cuenta Gratis' : 'Create Free Account'}
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        {isSpanish 
                          ? 'Obtén 5 frases al día gratis, o hazte VIP para acceso ilimitado'
                          : 'Get 5 phrases per day free, or go VIP for unlimited access'}
                      </p>
                    </>
                  ) : (
                    <>
                      <Button 
                        size="lg" 
                        onClick={() => navigate('/account')}
                        className="gap-2"
                        data-testid="go-vip-btn"
                      >
                        <Sparkles className="h-4 w-4" />
                        {t('subscribePerMonth')}
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        {t('sevenDayTrialIncluded')}
                      </p>
                    </>
                  )}
                </div>
              </CardHeader>
            </Card>
          )}
          
          {/* Phrase content - blurred if limit reached */}
          <div className={!canView ? 'blur-md select-none pointer-events-none' : ''}>
            {isMultiCategory && groupedData ? (
              <MultiCategoryPhraseDetail phraseData={groupedData} />
            ) : (
              <PhraseDetail phrase={phrase} />
            )}
          </div>
          
          {/* Navigation */}
          <div className="flex items-center justify-between mt-12 pt-8 border-t">
            {prevPhrase ? (
              <Button
                onClick={() => navigate(`/phrase/${prevPhrase.id}`)}
                className="flex items-center space-x-2 bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-medium"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{t('previous')} {t('phrase')}</span>
                <span className="sm:hidden">{t('previous')}</span>
              </Button>
            ) : (
              <div></div>
            )}
            
            {nextPhrase ? (
              <Button
                onClick={() => navigate(`/phrase/${nextPhrase.id}`)}
                className="flex items-center space-x-2 ml-auto bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-medium"
              >
                <span className="hidden sm:inline">{t('next')} {t('phrase')}</span>
                <span className="sm:hidden">{t('next')}</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <div></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhraseDetailPage;
